/**
 * WebGL per-pixel fusion of up to N station rasters into a single canvas —
 * the actual "merge in the client" implementation for workers/CONTRACTS.md's
 * 2026-08-10 "no server mosaic" decision. One draw call, one fullscreen
 * quad; the fragment shader (fusionShader.ts) samples every bound station's
 * raw-value texture and resolves overlaps per `BlendMode`, THEN colorizes
 * via the same palette LUT client/src/palette.ts already computes (blending
 * happens on raw quantized values, not pre-colorized RGBA — see
 * fusionDecodeProtocol.ts's doc comment on why).
 *
 * Targets a plain "webgl" (WebGL1) context primarily — LUMINANCE textures
 * and GLSL ES 1.00 work identically on both webgl and webgl2, so there is
 * no version-specific code path to maintain, and WebGL1 has the widest
 * baseline support. fusionController.ts falls back to the legacy painter's-
 * order per-station composite (kept for exactly this) if no GL context can
 * be created at all.
 */
import { compileProgram, buildFragmentShaderSource, VERTEX_SHADER_SOURCE, BLEND_MODE_INDEX } from "./fusionShader";
import type { Bounds3857 } from "./fusionGeo";
import { PALETTE_LUT, dbzToValue } from "./palette";

export type BlendMode = "nearest" | "max" | "owner-fallback";
export const BLEND_MODES: readonly BlendMode[] = ["nearest", "max", "owner-fallback"];

/** Default fused-view display floor (HUD toggle, app.ts): hide raw returns
 * below 10 dBZ — mostly sub-10 dBZ biological/ground-clutter speckle, not
 * precip (research/reports/first-look.md's RHO study: the 0.30-0.60 "clutter
 * / biology" band is exactly this kind of low-dBZ, low-rho noise). Flooring
 * happens per-station, in the shader, BEFORE the blend rule runs (see
 * fusionShader.ts's stationEvalBlock/ownerAccumBlock doc comments) — a
 * station whose only value at a pixel is sub-floor noise must look like
 * nodata to owner-fallback, not win outright over a farther station's real
 * return. `dbzToValue` (palette.ts) is the single source of truth for the
 * dBZ->raw-quantized-byte contract (data-model.md / bake-core's
 * quantize_dbz) — reused here rather than re-derived. */
export const PRECIP_FLOOR_DBZ = 10;
export const PRECIP_FLOOR_RAW = dbzToValue(PRECIP_FLOOR_DBZ);
/** "All returns" mode: only the shared nodata sentinel (raw === 0) excludes
 * a sample — same as the pre-floor behavior, and what station drill-down
 * always shows regardless of this toggle. */
export const NO_FLOOR_RAW = 0;

export interface StationInput {
  raw: Uint8Array;
  width: number;
  height: number;
  bbox: Bounds3857;
  /** EPSG:3857 station center (lat/lon projected once — fusionController.ts). */
  center: [number, number];
}

type GL = WebGLRenderingContext | WebGL2RenderingContext;

interface StationUniforms {
  tex: WebGLUniformLocation;
  uv: WebGLUniformLocation;
  center: WebGLUniformLocation;
  active: WebGLUniformLocation;
}

/** Caps a texture-unit-hungry design (maxStations station textures + 1
 * palette texture) at what this GL implementation actually guarantees,
 * rather than assuming the common (but not spec-mandated) 16+ units every
 * real browser provides — WebGL1's floor is only 8 total. */
function maxSafeStationCount(gl: GL, requested: number): number {
  const units = gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS) as number;
  return Math.max(0, Math.min(requested, units - 1)); // -1 reserves a unit for the palette
}

export class FusionCompositor {
  readonly canvas: HTMLCanvasElement;
  private gl: GL;
  private program: WebGLProgram;
  readonly maxStations: number;

  private positionBuffer: WebGLBuffer;
  private paletteTexture: WebGLTexture;
  private stationTextures: (WebGLTexture | null)[];
  private stationDims: Array<{ width: number; height: number } | null>;

  private uWindowOrigin: WebGLUniformLocation;
  private uWindowSize: WebGLUniformLocation;
  private uBlendMode: WebGLUniformLocation;
  private uPalette: WebGLUniformLocation;
  private uFloor: WebGLUniformLocation;
  private stationUniforms: StationUniforms[];

  constructor(maxStationsHint: number) {
    this.canvas = document.createElement("canvas");
    const gl = (this.canvas.getContext("webgl") ?? this.canvas.getContext("webgl2")) as GL | null;
    if (!gl) throw new Error("WebGL unavailable");
    this.gl = gl;

    this.maxStations = maxSafeStationCount(gl, maxStationsHint);
    this.program = compileProgram(gl, VERTEX_SHADER_SOURCE, buildFragmentShaderSource(this.maxStations));
    gl.useProgram(this.program);

    // Fullscreen quad: two triangles via a 4-vertex TRIANGLE_STRIP.
    const positions = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);
    const buf = gl.createBuffer();
    if (!buf) throw new Error("gl.createBuffer failed");
    this.positionBuffer = buf;
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);
    const posLoc = gl.getAttribLocation(this.program, "a_position");
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    this.paletteTexture = this.createPaletteTexture();
    this.stationTextures = new Array(this.maxStations).fill(null);
    this.stationDims = new Array(this.maxStations).fill(null);

    this.uWindowOrigin = this.mustGetUniform("u_windowOrigin");
    this.uWindowSize = this.mustGetUniform("u_windowSize");
    this.uBlendMode = this.mustGetUniform("u_blendMode");
    this.uPalette = this.mustGetUniform("u_palette");
    this.uFloor = this.mustGetUniform("u_floor");
    this.stationUniforms = Array.from({ length: this.maxStations }, (_, i) => ({
      tex: this.mustGetUniform(`u_tex${i}`),
      uv: this.mustGetUniform(`u_uv${i}`),
      center: this.mustGetUniform(`u_center${i}`),
      active: this.mustGetUniform(`u_active${i}`),
    }));
  }

  private mustGetUniform(name: string): WebGLUniformLocation {
    const loc = this.gl.getUniformLocation(this.program, name);
    if (!loc) throw new Error(`uniform ${name} not found (dead-code-eliminated by the GLSL compiler, or a real bug)`);
    return loc;
  }

  private createPaletteTexture(): WebGLTexture {
    const gl = this.gl;
    const tex = gl.createTexture();
    if (!tex) throw new Error("gl.createTexture failed");
    gl.bindTexture(gl.TEXTURE_2D, tex);
    // PALETTE_LUT is already the exact 256-entry RGBA table palette.ts uses
    // for the per-station/composite canvas path — reused verbatim so fusion
    // colorizes identically, not a second hand-maintained copy of the ramp.
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 256, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, PALETTE_LUT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return tex;
  }

  /** Uploads (or clears, with `input=null`) the raw-value texture for a
   * fixed slot (fusionController.ts assigns slot = STATIONS index, stable
   * for the app's lifetime, so this always reuses the same WebGLTexture
   * object rather than churning allocations on every ~10s frame update). */
  setStationTexture(slot: number, input: StationInput | null): void {
    const gl = this.gl;
    if (slot < 0 || slot >= this.maxStations) return; // clamped by maxSafeStationCount — silently drop extras
    if (!input) {
      this.stationDims[slot] = null;
      return;
    }
    let tex = this.stationTextures[slot];
    if (!tex) {
      const created = gl.createTexture();
      if (!created) throw new Error("gl.createTexture failed");
      tex = created;
      this.stationTextures[slot] = tex;
    }
    gl.bindTexture(gl.TEXTURE_2D, tex);
    // NEAREST, not LINEAR: these are quantized measurement bytes (raw dBZ
    // encoding, palette.ts), not photographic pixels — interpolating them
    // would blend physically meaningless values across bins/station edges.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, input.width, input.height, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, input.raw);
    this.stationDims[slot] = { width: input.width, height: input.height };
  }

  /** Renders the current set of station textures, blended per `blendMode`,
   * into `this.canvas` at `outputWidth`x`outputHeight` covering `window`
   * (EPSG:3857). `stationMeta` gives each active slot's UV transform + real
   * center — geometry-only, cheap to recompute every call (fusionGeo.ts).
   * `floorRaw` is a raw quantized-byte threshold (e.g. `PRECIP_FLOOR_RAW`,
   * or `NO_FLOOR_RAW` for "all returns") — normalized to the shader's [0,1]
   * texture-sample space here so callers stay in the same raw-byte units
   * the rest of the pipeline (palette.ts, fusionDecodeProtocol.ts) uses. */
  render(
    window: Bounds3857,
    blendMode: BlendMode,
    floorRaw: number,
    outputWidth: number,
    outputHeight: number,
    stationMeta: ReadonlyArray<{ uv: { scaleX: number; scaleY: number; offsetX: number; offsetY: number }; center: [number, number] } | null>,
  ): void {
    const gl = this.gl;
    if (this.canvas.width !== outputWidth || this.canvas.height !== outputHeight) {
      this.canvas.width = outputWidth;
      this.canvas.height = outputHeight;
    }
    gl.viewport(0, 0, outputWidth, outputHeight);
    gl.useProgram(this.program);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.uniform2f(this.uWindowOrigin, window.minX, window.maxY);
    gl.uniform2f(this.uWindowSize, window.maxX - window.minX, window.maxY - window.minY);
    gl.uniform1i(this.uBlendMode, BLEND_MODE_INDEX[blendMode]);
    gl.uniform1f(this.uFloor, floorRaw / 255);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.paletteTexture);
    gl.uniform1i(this.uPalette, 0);

    for (let i = 0; i < this.maxStations; i++) {
      const unit = i + 1; // texture unit 0 is the palette
      const u = this.stationUniforms[i];
      const meta = stationMeta[i];
      const tex = this.stationTextures[i];
      const hasData = !!(meta && tex && this.stationDims[i]);

      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, hasData ? tex : null);
      gl.uniform1i(u.tex, unit);
      gl.uniform1f(u.active, hasData ? 1 : 0);
      if (hasData && meta) {
        gl.uniform4f(u.uv, meta.uv.scaleX, meta.uv.scaleY, meta.uv.offsetX, meta.uv.offsetY);
        gl.uniform2f(u.center, meta.center[0], meta.center[1]);
      }
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  dispose(): void {
    const gl = this.gl;
    gl.deleteProgram(this.program);
    gl.deleteBuffer(this.positionBuffer);
    gl.deleteTexture(this.paletteTexture);
    for (const tex of this.stationTextures) if (tex) gl.deleteTexture(tex);
  }
}
